import { useState } from "react";
import { Search } from "lucide-react";
import LocationPanel from "./LocationPanel";
import DatePanel from "./DatePanel";
import GuestsPanel from "./GuestsPanel";
import { useRecentSearches } from "../../hooks/useRecentSearches";

function formatDateShort(str) {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return d.toLocaleDateString("gl-ES", { day: "numeric", month: "short" });
}

function formatDateRange(from, to) {
  if (!from && !to) return null;
  if (from && !to) return formatDateShort(from);
  return `${formatDateShort(from)} – ${formatDateShort(to)}`;
}

export default function SearchBar({ value = {}, onChange, onSearch, className = "" }) {
  const [activePanel, setActivePanel] = useState(null); // null | "location" | "dates" | "quantity"
  const { add: addRecent } = useRecentSearches();

  const { location, dateFrom, dateTo, quantity = {} } = value;

  function handlePanelClick(panel) {
    setActivePanel((prev) => (prev === panel ? null : panel));
  }

  function closeAll() {
    setActivePanel(null);
  }

  function handleLocationChange(loc) {
    onChange({ ...value, location: loc });
  }

  function handleDateChange({ dateFrom: df, dateTo: dt }) {
    onChange({ ...value, dateFrom: df, dateTo: dt });
    if (df && dt) setActivePanel("quantity");
  }

  function handleQuantityChange(qty) {
    onChange({ ...value, quantity: qty });
  }

  function handleSearch(e) {
    e.stopPropagation();
    closeAll();
    // X-4.4: gardar localización en recents se non está baleira.
    if (location && typeof location === "string" && location.trim()) {
      addRecent(location);
    }
    onSearch?.();
  }

  const isExpanded = activePanel !== null;

  // Summary labels
  const locationLabel = location || "Onde";
  const dateLabel = formatDateRange(dateFrom, dateTo) || "Cando";
  const quantityLabel =
    quantity?.listingType && quantity.listingType !== "Ambos"
      ? quantity.listingType
      : "Tipo";

  return (
    <>
      {/* Backdrop */}
      {isExpanded && (
        <div
          className="fixed inset-0 bg-transparent z-40"
          onClick={closeAll}
          aria-hidden="true"
        />
      )}

      {/* Search bar — hidden on mobile */}
      <div
        className={[
          "hidden md:flex relative items-center z-50",
          className,
        ].join(" ")}
      >
        <div
          className={[
            "flex items-center bg-white transition-all duration-200",
            isExpanded
              ? "rounded-[2rem] shadow-card-hover border border-border divide-x divide-border"
              : "rounded-full shadow-md border border-border hover:shadow-lg",
          ].join(" ")}
        >
          {/* ── Panel 1: Location ── */}
          <div className="relative">
            <button
              type="button"
              onClick={() => handlePanelClick("location")}
              className={[
                "flex flex-col px-6 py-3 rounded-full transition-colors text-left min-w-[160px]",
                activePanel === "location"
                  ? "bg-white shadow-sm rounded-full"
                  : "hover:bg-subtle",
              ].join(" ")}
            >
              <span className="text-xs font-semibold text-dark">Onde</span>
              <span
                className={[
                  "text-sm mt-0.5 truncate max-w-[140px]",
                  location ? "text-dark" : "text-muted",
                ].join(" ")}
              >
                {locationLabel}
              </span>
            </button>

            {activePanel === "location" && (
              <LocationPanel
                value={location}
                onChange={handleLocationChange}
                onClose={() => setActivePanel("dates")}
              />
            )}
          </div>

          {/* ── Panel 2: Dates ── */}
          <div className="relative">
            <button
              type="button"
              onClick={() => handlePanelClick("dates")}
              className={[
                "flex flex-col px-6 py-3 rounded-full transition-colors text-left min-w-[160px]",
                activePanel === "dates"
                  ? "bg-white shadow-sm rounded-full"
                  : "hover:bg-subtle",
              ].join(" ")}
            >
              <span className="text-xs font-semibold text-dark">Cando</span>
              <span
                className={[
                  "text-sm mt-0.5 truncate max-w-[140px]",
                  dateFrom ? "text-dark" : "text-muted",
                ].join(" ")}
              >
                {dateLabel}
              </span>
            </button>

            {activePanel === "dates" && (
              <DatePanel
                dateFrom={dateFrom}
                dateTo={dateTo}
                onChange={handleDateChange}
                onClose={() => setActivePanel("quantity")}
              />
            )}
          </div>

          {/* ── Panel 3: Quantity ── */}
          <div className="relative flex items-center pr-2">
            <button
              type="button"
              onClick={() => handlePanelClick("quantity")}
              className={[
                "flex flex-col px-6 py-3 rounded-full transition-colors text-left min-w-[140px]",
                activePanel === "quantity"
                  ? "bg-white shadow-sm rounded-full"
                  : "hover:bg-subtle",
              ].join(" ")}
            >
              <span className="text-xs font-semibold text-dark">Tipo</span>
              <span
                className={[
                  "text-sm mt-0.5 truncate max-w-[120px]",
                  quantity?.listingType && quantity.listingType !== "Ambos" ? "text-dark" : "text-muted",
                ].join(" ")}
              >
                {quantityLabel}
              </span>
            </button>

            {activePanel === "quantity" && (
              <GuestsPanel
                value={quantity}
                onChange={handleQuantityChange}
                onClose={closeAll}
              />
            )}

            {/* Search button */}
            <button
              type="button"
              onClick={handleSearch}
              aria-label="Buscar"
              className={[
                "flex items-center justify-center rounded-full bg-rauxa text-white hover:bg-rauxa/90 transition-all duration-200 flex-shrink-0",
                isExpanded ? "w-12 h-12 ml-1" : "w-8 h-8 ml-0",
              ].join(" ")}
            >
              <Search size={isExpanded ? 18 : 14} strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* Collapsed one-pill label (when no panel is active and bar is minimal) */}
        {!isExpanded && (
          <div
            className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none"
            aria-hidden="true"
          />
        )}
      </div>
    </>
  );
}
